import { Stack, Typography, ButtonBase } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/elements/Button";
import { Switch } from "@/components/elements/Switch";
import { SettingsCard } from "@/components/commons/settings";
import { IconStudySetting } from "@/assets/icons";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";
import { UpdateStudySettingsPayload } from "@/infrastructure/adapters/user-preference/user-preference.types";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { safeArea, rounded, spacing } from "@/theme/variables";
import { showError } from "@/utils/error";
import { calculateDailyHoursFromWeeklyMinutes, calculateWeeklyMinutesFromDailyHours, isValidDailyHours, parseDailyHoursInput } from "@/features/dashboard/utils/studyTime";
import { DAILY_BUTTON_PRESETS, DEFAULT_DAILY_STUDY_HOURS } from "@/features/dashboard/constants/studyTime";
import { DailyStudyHoursInput } from "@/features/dashboard/components/DailyStudyHoursInput";

type LocalSettingsState = Omit<UpdateStudySettingsPayload, "weeklyStudyTimeTarget">;

type TStudySettingsContentProps = {
  /** If provided, called after successful save instead of navigating to study page */
  onSaved?: () => void;
};

export const StudySettingsContent: FC<TStudySettingsContentProps> = ({ onSaved }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings, updateSettings, isUpdating } = useStudySettings();
  const [localSettings, setLocalSettings] = useState<LocalSettingsState>({});
  const [inputTouched, setInputTouched] = useState(false);
  const [dailyHoursInput, setDailyHoursInput] = useState<string>("");

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        isFuriganaEnabled: settings.isFuriganaEnabled,
        isTranslationEnabled: settings.isTranslationEnabled,
      });
      if (settings.weeklyStudyTimeTarget != null) {
        const dailyHrs = calculateDailyHoursFromWeeklyMinutes(settings.weeklyStudyTimeTarget);
        setDailyHoursInput(parseFloat(dailyHrs.toFixed(2)).toString());
      } else {
        setDailyHoursInput("");
      }
      setInputTouched(false);
    }
  }, [settings]);

  const dailyTargetNum = dailyHoursInput === "" ? DEFAULT_DAILY_STUDY_HOURS : parseDailyHoursInput(dailyHoursInput);

  const dailyStudyTimeErrorMessage = useMemo(() => {
    if (!inputTouched) return null;
    if (dailyHoursInput === "") return null;

    if (!isValidDailyHours(dailyTargetNum)) {
      return t("studySettings.studyTime.invalid_daily_target_error");
    }

    return null;
  }, [dailyHoursInput, dailyTargetNum, inputTouched, t]);

  const setDailyHoursFromButton = (hours: number) => {
    setDailyHoursInput(hours.toString());
    setInputTouched(true);
  };

  const buttonOptions = useMemo(() => {
    return DAILY_BUTTON_PRESETS.map((button) => ({
      value: button.value,
      color: button.colorKey,
      label: t(`studySettings.studyTime.${button.labelKey}`),
      subLabel: t("studySettings.studyTime.button_preset_sub", { count: button.recommendedHour }),
    }));
  }, [t]);

  const handleSave = async () => {
    if (dailyStudyTimeErrorMessage || !isValidDailyHours(dailyTargetNum)) {
      return;
    }
    if (isUpdating) return; // Prevent double-click
    try {
      await updateSettings({
        ...localSettings,
        weeklyStudyTimeTarget: calculateWeeklyMinutesFromDailyHours(dailyTargetNum),
        isBoostVisible: true,
      });
      if (onSaved) {
        onSaved();
      } else {
        navigate(AppRoutes.study, { replace: true });
      }
    } catch (error) {
      showError(error);
    }
  };

  const isSaveDisabled = isUpdating || !!dailyStudyTimeErrorMessage || !isValidDailyHours(dailyTargetNum);

  if (!settings) {
    return null;
  }

  return (
    <Stack flex={1} gap={2} alignItems="center">
      <Typography sx={{ ...typography.head20Bold, color: text.primary }}>irohana studyへようこそ！</Typography>

      <Stack width="100%" maxWidth="400px" height="100%" gap={2}>
        {/* Settings Card */}
        <SettingsCard icon={<IconStudySetting fontSize="24px" />} title={t("studySettings.title")}>
          <Stack gap={2.5}>
            <Switch
              nonControl
              label={t("studySettings.wordStudy.furiganaLabel")}
              labelVariant="body14Bold"
              checked={localSettings.isFuriganaEnabled ?? false}
              onChange={(_, checked) => setLocalSettings((prev) => ({ ...prev, isFuriganaEnabled: checked }))}
            />
            <Switch
              nonControl
              label={t("studySettings.practice.translationLabel")}
              labelVariant="body14Bold"
              checked={localSettings.isTranslationEnabled ?? false}
              onChange={(_, checked) => setLocalSettings((prev) => ({ ...prev, isTranslationEnabled: checked }))}
            />
          </Stack>
        </SettingsCard>

        {/* Study Time Section */}
        <Stack gap={1} flex={1}>
          <Typography sx={{ ...typography.body14Semi, color: text.primary }}>{t("studySettings.studyTime.title")}</Typography>

          <Stack gap={1}>
            <Typography sx={{ ...typography.cap12, color: text.primary }}>{t("studySettings.studyTime.label")}</Typography>
            <DailyStudyHoursInput
              value={dailyHoursInput}
              onChange={(val) => {
                setDailyHoursInput(val);
                setInputTouched((touched) => !!val || touched);
              }}
              onBlur={() => setInputTouched(true)}
              errorMessage={dailyStudyTimeErrorMessage}
            />
            <Stack direction="row" justifyContent="space-between" gap={spacing.md} px={spacing.sm} width="100%">
              {buttonOptions.map((option) => {
                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => setDailyHoursFromButton(option.value)}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      background: option.color,
                      borderRadius: rounded.sm,
                      py: spacing.xs,
                    }}
                  >
                    <Stack alignItems="center">
                      <Typography variant="cap12" color={text.white} textAlign="center">
                        {option.label}
                      </Typography>
                      <Typography variant="body14Semi" color={text.white} textAlign="center">
                        {option.subLabel}
                      </Typography>
                    </Stack>
                  </ButtonBase>
                );
              })}
            </Stack>
          </Stack>
        </Stack>

        {/* Action Button — fixed at bottom, constrained to layout max-width */}
        <Stack width="100%" maxWidth="360px" p={2} pb={`calc(${safeArea.bottom} + 16px)`} margin="0 auto">
          <Button fullWidth onClick={handleSave} isLoading={isUpdating} disabled={isSaveDisabled}>
            {t("studySettings.submit")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
